﻿/*!
    *
    * Wijmo Library 5.20251.31
    * https://developer.mescius.com/wijmo
    *
    * Copyright(c) MESCIUS inc. All rights reserved.
    *
    * Licensed under the End-User License Agreement For MESCIUS Wijmo Software.
    * us.sales@mescius.com
    * https://developer.mescius.com/wijmo/licensing
    *
    */
var __extends = (this && this.__extends) || (function () {
    var extendStatics = function (d, b) {
        extendStatics = Object.setPrototypeOf ||
            ({ __proto__: [] } instanceof Array && function (d, b) { d.__proto__ = b; }) ||
            function (d, b) { for (var p in b) if (b.hasOwnProperty(p)) d[p] = b[p]; };
        return extendStatics(d, b);
    };
    return function (d, b) {
        extendStatics(d, b);
        function __() { this.constructor = d; }
        d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
    };
})();
import { VueApi, WjComponentBehavior } from '@mescius/wijmo.vue2.base';
import * as wjcCore from '@mescius/wijmo';
import * as wjcInput from '@mescius/wijmo.input';
var WjListBoxBehavior = /** @class */ (function (_super) {
    __extends(WjListBoxBehavior, _super);
    function WjListBoxBehavior() {
        return _super !== null && _super.apply(this, arguments) || this;
    }
    WjListBoxBehavior.tag = 'wj-list-box';
    WjListBoxBehavior.props = [
        'isDisabled',
        'tabOrder',
        'isContentHtml',
        'maxHeight',
        'selectedValuePath',
        'itemFormatter',
        'displayMemberPath',
        'checkedMemberPath',
        'caseSensitiveSearch',
        'itemsSource',
        'virtualizationThreshold',
        'showGroups',
        'selectedIndex',
        'selectedItem',
        'selectedValue',
        'checkedItems'
    ];
    WjListBoxBehavior.events = [
        'initialized',
        'gotFocus',
        'lostFocus',
        'refreshing',
        'refreshed',
        'invalidInput',
        'formatItem',
        'itemsChanged',
        'itemChecked'
    ];
    WjListBoxBehavior.changeEvents = {
        'selectedIndexChanged': ['selectedIndex', 'selectedItem', 'selectedValue'],
        'checkedItemsChanged': ['checkedItems'],
    };
    WjListBoxBehavior.classCtor = function () { return wjcInput.ListBox; };
    WjListBoxBehavior.modelProp = 'selectedValue';
    return WjListBoxBehavior;
}(WjComponentBehavior));
/**
 * Vue component for the {@link wijmo.input.ListBox} control.
 *
 * The <b>wj-list-box</b> component may contain
 * a {@link wijmo.vue2.input.WjItemTemplate} child component.
 *
 * The component supports all properties and events of the pure JavaScript {@link wijmo.input.ListBox} control it represents.
 *
 * The component includes an <b>initialized</b> event that is raised when the control is initialized after it is added to the page.
 * You can use this event to perform further initialization in addition to setting properties in markup.
 * The signature of the handler function is the same as any other Wijmo event handlers.
 */
export var WjListBox = WjListBoxBehavior.register();
function registerV3WjListBox(app) {
    app.component(WjListBoxBehavior.tag, WjListBox);
}
var WjMultiSelectListBoxBehavior = /** @class */ (function (_super) {
    __extends(WjMultiSelectListBoxBehavior, _super);
    function WjMultiSelectListBoxBehavior() {
        return _super !== null && _super.apply(this, arguments) || this;
    }
    WjMultiSelectListBoxBehavior.tag = 'wj-multi-select-list-box';
    WjMultiSelectListBoxBehavior.props = [
        'isDisabled',
        'tabOrder',
        'itemsSource',
        'displayMemberPath',
        'selectedIndex',
        'isContentHtml',
        'showGroups',
        'checkOnFilter',
        'showFilterInput',
        'filterInputPlaceholder',
        'showSelectAllCheckbox',
        'selectAllLabel',
        'delay',
        'caseSensitiveSearch',
        'checkedMemberPath',
        'virtualizationThreshold',
        'checkedItems'
    ];
    WjMultiSelectListBoxBehavior.events = [
        'initialized',
        'gotFocus',
        'lostFocus',
        'refreshing',
        'refreshed',
        'invalidInput'
    ];
    WjMultiSelectListBoxBehavior.changeEvents = {
        'selectedIndexChanged': ['selectedIndex'],
        'checkedItemsChanged': ['checkedItems'],
    };
    WjMultiSelectListBoxBehavior.classCtor = function () { return wjcInput.MultiSelectListBox; };
    WjMultiSelectListBoxBehavior.modelProp = 'checkedItems';
    return WjMultiSelectListBoxBehavior;
}(WjComponentBehavior));
/**
 * Vue component for the {@link wijmo.input.MultiSelectListBox} control.
 *
 * The component supports all properties and events of the pure JavaScript {@link wijmo.input.MultiSelectListBox} control it represents.
 *
 * The component includes an <b>initialized</b> event that is raised when the control is initialized after it is added to the page.
 * You can use this event to perform further initialization in addition to setting properties in markup.
 * The signature of the handler function is the same as any other Wijmo event handlers.
 */
export var WjMultiSelectListBox = WjMultiSelectListBoxBehavior.register();
function registerV3WjMultiSelectListBox(app) {
    app.component(WjMultiSelectListBoxBehavior.tag, WjMultiSelectListBox);
}
var WjComboBoxBehavior = /** @class */ (function (_super) {
    __extends(WjComboBoxBehavior, _super);
    function WjComboBoxBehavior() {
        return _super !== null && _super.apply(this, arguments) || this;
    }
    WjComboBoxBehavior.tag = 'wj-combo-box';
    WjComboBoxBehavior.props = [
        'isDisabled',
        'tabOrder',
        'isDroppedDown',
        'showDropDownButton',
        'autoExpandSelection',
        'placeholder',
        'dropDownCssClass',
        'isAnimated',
        'isReadOnly',
        'isRequired',
        'inputType',
        'clickAction',
        'displayMemberPath',
        'selectedValuePath',
        'headerPath',
        'isContentHtml',
        'isEditable',
        'handleWheel',
        'maxDropDownHeight',
        'maxDropDownWidth',
        'itemFormatter',
        'showGroups',
        'trimText',
        'caseSensitiveSearch',
        'virtualizationThreshold',
        'itemsSource',
        'text',
        'selectedIndex',
        'selectedItem',
        'selectedValue'
    ];
    WjComboBoxBehavior.events = [
        'initialized',
        'gotFocus',
        'lostFocus',
        'refreshing',
        'refreshed',
        'invalidInput',
        'isDroppedDownChanging',
        'itemsSourceChanged',
        'formatItem'
    ];
    WjComboBoxBehavior.changeEvents = {
        'isDroppedDownChanged': ['isDroppedDown'],
        'textChanged': ['text'],
        'selectedIndexChanged': ['selectedIndex', 'selectedItem', 'selectedValue'],
    };
    WjComboBoxBehavior.classCtor = function () { return wjcInput.ComboBox; };
    WjComboBoxBehavior.modelProp = 'selectedValue';
    return WjComboBoxBehavior;
}(WjComponentBehavior));
/**
 * Vue component for the {@link wijmo.input.ComboBox} control.
 *
 * The <b>wj-combo-box</b> component may contain
 * a {@link wijmo.vue2.input.WjItemTemplate} child component.
 *
 * The component supports all properties and events of the pure JavaScript {@link wijmo.input.ComboBox} control it represents.
 *
 * The component includes an <b>initialized</b> event that is raised when the control is initialized after it is added to the page.
 * You can use this event to perform further initialization in addition to setting properties in markup.
 * The signature of the handler function is the same as any other Wijmo event handlers.
 */
export var WjComboBox = WjComboBoxBehavior.register();
function registerV3WjComboBox(app) {
    app.component(WjComboBoxBehavior.tag, WjComboBox);
}
var WjAutoCompleteBehavior = /** @class */ (function (_super) {
    __extends(WjAutoCompleteBehavior, _super);
    function WjAutoCompleteBehavior() {
        return _super !== null && _super.apply(this, arguments) || this;
    }
    WjAutoCompleteBehavior.tag = 'wj-auto-complete';
    WjAutoCompleteBehavior.props = [
        'isDisabled',
        'tabOrder',
        'isDroppedDown',
        'showDropDownButton',
        'autoExpandSelection',
        'placeholder',
        'dropDownCssClass',
        'isAnimated',
        'isReadOnly',
        'isRequired',
        'inputType',
        'clickAction',
        'displayMemberPath',
        'selectedValuePath',
        'headerPath',
        'isContentHtml',
        'isEditable',
        'handleWheel',
        'maxDropDownHeight',
        'maxDropDownWidth',
        'itemFormatter',
        'showGroups',
        'trimText',
        'caseSensitiveSearch',
        'virtualizationThreshold',
        'delay',
        'maxItems',
        'minLength',
        'cssMatch',
        'itemsSourceFunction',
        'searchMemberPath',
        'beginsWithSearch',
        'itemsSource',
        'text',
        'selectedIndex',
        'selectedItem',
        'selectedValue'
    ];
    WjAutoCompleteBehavior.events = [
        'initialized',
        'gotFocus',
        'lostFocus',
        'refreshing',
        'refreshed',
        'invalidInput',
        'isDroppedDownChanging',
        'itemsSourceChanged',
        'formatItem'
    ];
    WjAutoCompleteBehavior.changeEvents = {
        'isDroppedDownChanged': ['isDroppedDown'],
        'textChanged': ['text'],
        'selectedIndexChanged': ['selectedIndex', 'selectedItem', 'selectedValue'],
    };
    WjAutoCompleteBehavior.classCtor = function () { return wjcInput.AutoComplete; };
    WjAutoCompleteBehavior.modelProp = 'selectedValue';
    return WjAutoCompleteBehavior;
}(WjComponentBehavior));
/**
 * Vue component for the {@link wijmo.input.AutoComplete} control.
 *
 * The component supports all properties and events of the pure JavaScript {@link wijmo.input.AutoComplete} control it represents.
 *
 * The component includes an <b>initialized</b> event that is raised when the control is initialized after it is added to the page.
 * You can use this event to perform further initialization in addition to setting properties in markup.
 * The signature of the handler function is the same as any other Wijmo event handlers.
 */
export var WjAutoComplete = WjAutoCompleteBehavior.register();
function registerV3WjAutoComplete(app) {
    app.component(WjAutoCompleteBehavior.tag, WjAutoComplete);
}
var WjCalendarBehavior = /** @class */ (function (_super) {
    __extends(WjCalendarBehavior, _super);
    function WjCalendarBehavior() {
        return _super !== null && _super.apply(this, arguments) || this;
    }
    WjCalendarBehavior.tag = 'wj-calendar';
    WjCalendarBehavior.props = [
        'isDisabled',
        'tabOrder',
        'monthView',
        'showHeader',
        'itemFormatter',
        'itemValidator',
        'firstDayOfWeek',
        'max',
        'min',
        'formatYearMonth',
        'formatDayHeaders',
        'formatDays',
        'formatYear',
        'formatMonths',
        'selectionMode',
        'isReadOnly',
        'handleWheel',
        'repeatButtons',
        'showYearPicker',
        'value',
        'displayMonth',
        'monthCount',
        'showMonthPicker',
        'weeksBefore',
        'weeksAfter',
        'rangeEnd',
        'rangeMin',
        'rangeMax'
    ];
    WjCalendarBehavior.events = [
        'initialized',
        'gotFocus',
        'lostFocus',
        'refreshing',
        'refreshed',
        'invalidInput',
        'rangeChanged',
        'formatItem'
    ];
    WjCalendarBehavior.changeEvents = {
        'valueChanged': ['value'],
        'displayMonthChanged': ['displayMonth'],
        'rangeEndChanged': ['rangeEnd'],
    };
    WjCalendarBehavior.classCtor = function () { return wjcInput.Calendar; };
    WjCalendarBehavior.modelProp = 'value';
    return WjCalendarBehavior;
}(WjComponentBehavior));
/**
 * Vue component for the {@link wijmo.input.Calendar} control.
 *
 * The component supports all properties and events of the pure JavaScript {@link wijmo.input.Calendar} control it represents.
 *
 * The component includes an <b>initialized</b> event that is raised when the control is initialized after it is added to the page.
 * You can use this event to perform further initialization in addition to setting properties in markup.
 * The signature of the handler function is the same as any other Wijmo event handlers.
 */
export var WjCalendar = WjCalendarBehavior.register();
function registerV3WjCalendar(app) {
    app.component(WjCalendarBehavior.tag, WjCalendar);
}
var WjColorPickerBehavior = /** @class */ (function (_super) {
    __extends(WjColorPickerBehavior, _super);
    function WjColorPickerBehavior() {
        return _super !== null && _super.apply(this, arguments) || this;
    }
    WjColorPickerBehavior.tag = 'wj-color-picker';
    WjColorPickerBehavior.props = [
        'isDisabled',
        'tabOrder',
        'showAlphaChannel',
        'showColorString',
        'palette',
        'value'
    ];
    WjColorPickerBehavior.events = [
        'initialized',
        'gotFocus',
        'lostFocus',
        'refreshing',
        'refreshed',
        'invalidInput'
    ];
    WjColorPickerBehavior.changeEvents = {
        'valueChanged': ['value'],
    };
    WjColorPickerBehavior.classCtor = function () { return wjcInput.ColorPicker; };
    WjColorPickerBehavior.modelProp = 'value';
    return WjColorPickerBehavior;
}(WjComponentBehavior));
/**
 * Vue component for the {@link wijmo.input.ColorPicker} control.
 *
 * The component supports all properties and events of the pure JavaScript {@link wijmo.input.ColorPicker} control it represents.
 *
 * The component includes an <b>initialized</b> event that is raised when the control is initialized after it is added to the page.
 * You can use this event to perform further initialization in addition to setting properties in markup.
 * The signature of the handler function is the same as any other Wijmo event handlers.
 */
export var WjColorPicker = WjColorPickerBehavior.register();
function registerV3WjColorPicker(app) {
    app.component(WjColorPickerBehavior.tag, WjColorPicker);
}
var WjInputMaskBehavior = /** @class */ (function (_super) {
    __extends(WjInputMaskBehavior, _super);
    function WjInputMaskBehavior() {
        return _super !== null && _super.apply(this, arguments) || this;
    }
    WjInputMaskBehavior.tag = 'wj-input-mask';
    WjInputMaskBehavior.props = [
        'isDisabled',
        'tabOrder',
        'mask',
        'overwriteMode',
        'isRequired',
        'isReadOnly',
        'promptChar',
        'placeholder',
        'inputType',
        'rawValue',
        'value'
    ];
    WjInputMaskBehavior.events = [
        'initialized',
        'gotFocus',
        'lostFocus',
        'refreshing',
        'refreshed',
        'invalidInput'
    ];
    WjInputMaskBehavior.changeEvents = {
        'valueChanged': ['rawValue', 'value'],
    };
    WjInputMaskBehavior.classCtor = function () { return wjcInput.InputMask; };
    WjInputMaskBehavior.modelProp = 'value';
    return WjInputMaskBehavior;
}(WjComponentBehavior));
/**
 * Vue component for the {@link wijmo.input.InputMask} control.
 *
 * The component supports all properties and events of the pure JavaScript {@link wijmo.input.InputMask} control it represents.
 *
 * The component includes an <b>initialized</b> event that is raised when the control is initialized after it is added to the page.
 * You can use this event to perform further initialization in addition to setting properties in markup.
 * The signature of the handler function is the same as any other Wijmo event handlers.
 */
export var WjInputMask = WjInputMaskBehavior.register();
function registerV3WjInputMask(app) {
    app.component(WjInputMaskBehavior.tag, WjInputMask);
}
var WjInputColorBehavior = /** @class */ (function (_super) {
    __extends(WjInputColorBehavior, _super);
    function WjInputColorBehavior() {
        return _super !== null && _super.apply(this, arguments) || this;
    }
    WjInputColorBehavior.tag = 'wj-input-color';
    WjInputColorBehavior.props = [
        'isDisabled',
        'tabOrder',
        'isDroppedDown',
        'showDropDownButton',
        'autoExpandSelection',
        'placeholder',
        'dropDownCssClass',
        'isAnimated',
        'isReadOnly',
        'isRequired',
        'inputType',
        'clickAction',
        'showAlphaChannel',
        'showColorString',
        'value',
        'text'
    ];
    WjInputColorBehavior.events = [
        'initialized',
        'gotFocus',
        'lostFocus',
        'refreshing',
        'refreshed',
        'invalidInput',
        'isDroppedDownChanging'
    ];
    WjInputColorBehavior.changeEvents = {
        'isDroppedDownChanged': ['isDroppedDown'],
        'textChanged': ['text'],
        'valueChanged': ['value'],
    };
    WjInputColorBehavior.classCtor = function () { return wjcInput.InputColor; };
    WjInputColorBehavior.modelProp = 'value';
    return WjInputColorBehavior;
}(WjComponentBehavior));
/**
 * Vue component for the {@link wijmo.input.InputColor} control.
 *
 * The component supports all properties and events of the pure JavaScript {@link wijmo.input.InputColor} control it represents.
 *
 * The component includes an <b>initialized</b> event that is raised when the control is initialized after it is added to the page.
 * You can use this event to perform further initialization in addition to setting properties in markup.
 * The signature of the handler function is the same as any other Wijmo event handlers.
 */
export var WjInputColor = WjInputColorBehavior.register();
function registerV3WjInputColor(app) {
    app.component(WjInputColorBehavior.tag, WjInputColor);
}
var WjMultiSelectBehavior = /** @class */ (function (_super) {
    __extends(WjMultiSelectBehavior, _super);
    function WjMultiSelectBehavior() {
        return _super !== null && _super.apply(this, arguments) || this;
    }
    WjMultiSelectBehavior.tag = 'wj-multi-select';
    WjMultiSelectBehavior.props = [
        'isDisabled',
        'tabOrder',
        'isDroppedDown',
        'showDropDownButton',
        'autoExpandSelection',
        'placeholder',
        'dropDownCssClass',
        'isAnimated',
        'isReadOnly',
        'isRequired',
        'inputType',
        'clickAction',
        'displayMemberPath',
        'selectedValuePath',
        'headerPath',
        'isContentHtml',
        'isEditable',
        'handleWheel',
        'maxDropDownHeight',
        'maxDropDownWidth',
        'itemFormatter',
        'showGroups',
        'trimText',
        'caseSensitiveSearch',
        'virtualizationThreshold',
        'checkedMemberPath',
        'maxHeaderItems',
        'headerFormat',
        'headerFormatter',
        'showSelectAllCheckbox',
        'selectAllLabel',
        'showFilterInput',
        'filterInputPlaceholder',
        'checkOnFilter',
        'delay',
        'caseSensitiveSearch',
        'itemsSource',
        'checkedItems',
        'text',
        'selectedIndex',
        'selectedItem',
        'selectedValue'
    ];
    WjMultiSelectBehavior.events = [
        'initialized',
        'gotFocus',
        'lostFocus',
        'refreshing',
        'refreshed',
        'invalidInput',
        'isDroppedDownChanging',
        'itemsSourceChanged',
        'formatItem'
    ];
    WjMultiSelectBehavior.changeEvents = {
        'isDroppedDownChanged': ['isDroppedDown'],
        'textChanged': ['text'],
        'selectedIndexChanged': ['selectedIndex', 'selectedItem', 'selectedValue'],
        'checkedItemsChanged': ['checkedItems'],
    };
    WjMultiSelectBehavior.classCtor = function () { return wjcInput.MultiSelect; };
    WjMultiSelectBehavior.modelProp = 'checkedItems';
    return WjMultiSelectBehavior;
}(WjComponentBehavior));
/**
 * Vue component for the {@link wijmo.input.MultiSelect} control.
 *
 * The <b>wj-multi-select</b> component may contain
 * a {@link wijmo.vue2.input.WjItemTemplate} child component.
 *
 * The component supports all properties and events of the pure JavaScript {@link wijmo.input.MultiSelect} control it represents.
 *
 * The component includes an <b>initialized</b> event that is raised when the control is initialized after it is added to the page.
 * You can use this event to perform further initialization in addition to setting properties in markup.
 * The signature of the handler function is the same as any other Wijmo event handlers.
 */
export var WjMultiSelect = WjMultiSelectBehavior.register();
function registerV3WjMultiSelect(app) {
    app.component(WjMultiSelectBehavior.tag, WjMultiSelect);
}
var WjMultiAutoCompleteBehavior = /** @class */ (function (_super) {
    __extends(WjMultiAutoCompleteBehavior, _super);
    function WjMultiAutoCompleteBehavior() {
        return _super !== null && _super.apply(this, arguments) || this;
    }
    WjMultiAutoCompleteBehavior.tag = 'wj-multi-auto-complete';
    WjMultiAutoCompleteBehavior.props = [
        'isDisabled',
        'tabOrder',
        'isDroppedDown',
        'showDropDownButton',
        'autoExpandSelection',
        'placeholder',
        'dropDownCssClass',
        'isAnimated',
        'isReadOnly',
        'isRequired',
        'inputType',
        'clickAction',
        'displayMemberPath',
        'selectedValuePath',
        'headerPath',
        'isContentHtml',
        'isEditable',
        'handleWheel',
        'maxDropDownHeight',
        'maxDropDownWidth',
        'itemFormatter',
        'showGroups',
        'trimText',
        'caseSensitiveSearch',
        'virtualizationThreshold',
        'delay',
        'maxItems',
        'minLength',
        'cssMatch',
        'itemsSourceFunction',
        'searchMemberPath',
        'beginsWithSearch',
        'maxSelectedItems',
        'selectedItems',
        'itemsSource',
        'selectedMemberPath',
        'text',
        'selectedIndex',
        'selectedItem',
        'selectedValue'
    ];
    WjMultiAutoCompleteBehavior.events = [
        'initialized',
        'gotFocus',
        'lostFocus',
        'refreshing',
        'refreshed',
        'invalidInput',
        'isDroppedDownChanging',
        'itemsSourceChanged',
        'formatItem'
    ];
    WjMultiAutoCompleteBehavior.changeEvents = {
        'isDroppedDownChanged': ['isDroppedDown'],
        'textChanged': ['text'],
        'selectedIndexChanged': ['selectedIndex', 'selectedItem', 'selectedValue'],
        'selectedItemsChanged': ['selectedItems'],
    };
    WjMultiAutoCompleteBehavior.classCtor = function () { return wjcInput.MultiAutoComplete; };
    WjMultiAutoCompleteBehavior.modelProp = 'selectedItems';
    return WjMultiAutoCompleteBehavior;
}(WjComponentBehavior));
/**
 * Vue component for the {@link wijmo.input.MultiAutoComplete} control.
 *
 * The component supports all properties and events of the pure JavaScript {@link wijmo.input.MultiAutoComplete} control it represents.
 *
 * The component includes an <b>initialized</b> event that is raised when the control is initialized after it is added to the page.
 * You can use this event to perform further initialization in addition to setting properties in markup.
 * The signature of the handler function is the same as any other Wijmo event handlers.
 */
export var WjMultiAutoComplete = WjMultiAutoCompleteBehavior.register();
function registerV3WjMultiAutoComplete(app) {
    app.component(WjMultiAutoCompleteBehavior.tag, WjMultiAutoComplete);
}
var WjInputNumberBehavior = /** @class */ (function (_super) {
    __extends(WjInputNumberBehavior, _super);
    function WjInputNumberBehavior() {
        return _super !== null && _super.apply(this, arguments) || this;
    }
    WjInputNumberBehavior.tag = 'wj-input-number';
    WjInputNumberBehavior.props = [
        'isDisabled',
        'tabOrder',
        'showSpinner',
        'repeatButtons',
        'max',
        'min',
        'step',
        'isRequired',
        'placeholder',
        'inputType',
        'format',
        'isReadOnly',
        'handleWheel',
        'value',
        'text'
    ];
    WjInputNumberBehavior.events = [
        'initialized',
        'gotFocus',
        'lostFocus',
        'refreshing',
        'refreshed',
        'invalidInput'
    ];
    WjInputNumberBehavior.changeEvents = {
        'valueChanged': ['value'],
        'textChanged': ['text'],
    };
    WjInputNumberBehavior.classCtor = function () { return wjcInput.InputNumber; };
    WjInputNumberBehavior.modelProp = 'value';
    return WjInputNumberBehavior;
}(WjComponentBehavior));
/**
 * Vue component for the {@link wijmo.input.InputNumber} control.
 *
 * The component supports all properties and events of the pure JavaScript {@link wijmo.input.InputNumber} control it represents.
 *
 * The component includes an <b>initialized</b> event that is raised when the control is initialized after it is added to the page.
 * You can use this event to perform further initialization in addition to setting properties in markup.
 * The signature of the handler function is the same as any other Wijmo event handlers.
 */
export var WjInputNumber = WjInputNumberBehavior.register();
function registerV3WjInputNumber(app) {
    app.component(WjInputNumberBehavior.tag, WjInputNumber);
}
var WjInputDateBehavior = /** @class */ (function (_super) {
    __extends(WjInputDateBehavior, _super);
    function WjInputDateBehavior() {
        return _super !== null && _super.apply(this, arguments) || this;
    }
    WjInputDateBehavior.tag = 'wj-input-date';
    WjInputDateBehavior.props = [
        'isDisabled',
        'tabOrder',
        'isDroppedDown',
        'showDropDownButton',
        'autoExpandSelection',
        'placeholder',
        'dropDownCssClass',
        'isAnimated',
        'isReadOnly',
        'isRequired',
        'inputType',
        'clickAction',
        'selectionMode',
        'format',
        'mask',
        'max',
        'min',
        'inputType',
        'repeatButtons',
        'showYearPicker',
        'itemValidator',
        'itemFormatter',
        'monthCount',
        'handleWheel',
        'showMonthPicker',
        'showHeader',
        'weeksBefore',
        'weeksAfter',
        'rangeMin',
        'rangeMax',
        'separator',
        'alwaysShowCalendar',
        'predefinedRanges',
        'closeOnSelection',
        'text',
        'value',
        'rangeEnd',
        'keepInvalidText',
        'preserveCalendarNavigation'
    ];
    WjInputDateBehavior.events = [
        'initialized',
        'gotFocus',
        'lostFocus',
        'refreshing',
        'refreshed',
        'invalidInput',
        'isDroppedDownChanging',
        'rangeChanged'
    ];
    WjInputDateBehavior.changeEvents = {
        'isDroppedDownChanged': ['isDroppedDown'],
        'textChanged': ['text'],
        'valueChanged': ['value'],
        'rangeEndChanged': ['rangeEnd'],
    };
    WjInputDateBehavior.classCtor = function () { return wjcInput.InputDate; };
    WjInputDateBehavior.modelProp = 'value';
    return WjInputDateBehavior;
}(WjComponentBehavior));
/**
 * Vue component for the {@link wijmo.input.InputDate} control.
 *
 * The component supports all properties and events of the pure JavaScript {@link wijmo.input.InputDate} control it represents.
 *
 * The component includes an <b>initialized</b> event that is raised when the control is initialized after it is added to the page.
 * You can use this event to perform further initialization in addition to setting properties in markup.
 * The signature of the handler function is the same as any other Wijmo event handlers.
 */
export var WjInputDate = WjInputDateBehavior.register();
function registerV3WjInputDate(app) {
    app.component(WjInputDateBehavior.tag, WjInputDate);
}
var WjInputTimeBehavior = /** @class */ (function (_super) {
    __extends(WjInputTimeBehavior, _super);
    function WjInputTimeBehavior() {
        return _super !== null && _super.apply(this, arguments) || this;
    }
    WjInputTimeBehavior.tag = 'wj-input-time';
    WjInputTimeBehavior.props = [
        'isDisabled',
        'tabOrder',
        'isDroppedDown',
        'showDropDownButton',
        'autoExpandSelection',
        'placeholder',
        'dropDownCssClass',
        'isAnimated',
        'isReadOnly',
        'isRequired',
        'inputType',
        'clickAction',
        'displayMemberPath',
        'selectedValuePath',
        'headerPath',
        'isContentHtml',
        'isEditable',
        'handleWheel',
        'maxDropDownHeight',
        'maxDropDownWidth',
        'itemFormatter',
        'showGroups',
        'trimText',
        'caseSensitiveSearch',
        'virtualizationThreshold',
        'max',
        'min',
        'step',
        'format',
        'mask',
        'inputType',
        'itemsSource',
        'text',
        'selectedIndex',
        'selectedItem',
        'selectedValue',
        'value'
    ];
    WjInputTimeBehavior.events = [
        'initialized',
        'gotFocus',
        'lostFocus',
        'refreshing',
        'refreshed',
        'invalidInput',
        'isDroppedDownChanging',
        'itemsSourceChanged',
        'formatItem'
    ];
    WjInputTimeBehavior.changeEvents = {
        'isDroppedDownChanged': ['isDroppedDown'],
        'textChanged': ['text'],
        'selectedIndexChanged': ['selectedIndex', 'selectedItem', 'selectedValue'],
        'valueChanged': ['value'],
    };
    WjInputTimeBehavior.classCtor = function () { return wjcInput.InputTime; };
    WjInputTimeBehavior.modelProp = 'value';
    return WjInputTimeBehavior;
}(WjComponentBehavior));
/**
 * Vue component for the {@link wijmo.input.InputTime} control.
 *
 * The component supports all properties and events of the pure JavaScript {@link wijmo.input.InputTime} control it represents.
 *
 * The component includes an <b>initialized</b> event that is raised when the control is initialized after it is added to the page.
 * You can use this event to perform further initialization in addition to setting properties in markup.
 * The signature of the handler function is the same as any other Wijmo event handlers.
 */
export var WjInputTime = WjInputTimeBehavior.register();
function registerV3WjInputTime(app) {
    app.component(WjInputTimeBehavior.tag, WjInputTime);
}
var WjInputDateTimeBehavior = /** @class */ (function (_super) {
    __extends(WjInputDateTimeBehavior, _super);
    function WjInputDateTimeBehavior() {
        return _super !== null && _super.apply(this, arguments) || this;
    }
    WjInputDateTimeBehavior.tag = 'wj-input-date-time';
    WjInputDateTimeBehavior.props = [
        'isDisabled',
        'tabOrder',
        'isDroppedDown',
        'showDropDownButton',
        'autoExpandSelection',
        'placeholder',
        'dropDownCssClass',
        'isAnimated',
        'isReadOnly',
        'isRequired',
        'inputType',
        'clickAction',
        'selectionMode',
        'format',
        'mask',
        'max',
        'min',
        'inputType',
        'repeatButtons',
        'showYearPicker',
        'itemValidator',
        'itemFormatter',
        'monthCount',
        'handleWheel',
        'showMonthPicker',
        'showHeader',
        'weeksBefore',
        'weeksAfter',
        'rangeMin',
        'rangeMax',
        'separator',
        'alwaysShowCalendar',
        'predefinedRanges',
        'closeOnSelection',
        'timeMax',
        'timeMin',
        'timeStep',
        'timeFormat',
        'text',
        'value',
        'rangeEnd',
        'preserveCalendarNavigation'
    ];
    WjInputDateTimeBehavior.events = [
        'initialized',
        'gotFocus',
        'lostFocus',
        'refreshing',
        'refreshed',
        'invalidInput',
        'isDroppedDownChanging',
        'rangeChanged'
    ];
    WjInputDateTimeBehavior.changeEvents = {
        'isDroppedDownChanged': ['isDroppedDown'],
        'textChanged': ['text'],
        'valueChanged': ['value'],
        'rangeEndChanged': ['rangeEnd'],
    };
    WjInputDateTimeBehavior.classCtor = function () { return wjcInput.InputDateTime; };
    WjInputDateTimeBehavior.modelProp = 'value';
    return WjInputDateTimeBehavior;
}(WjComponentBehavior));
/**
 * Vue component for the {@link wijmo.input.InputDateTime} control.
 *
 * The component supports all properties and events of the pure JavaScript {@link wijmo.input.InputDateTime} control it represents.
 *
 * The component includes an <b>initialized</b> event that is raised when the control is initialized after it is added to the page.
 * You can use this event to perform further initialization in addition to setting properties in markup.
 * The signature of the handler function is the same as any other Wijmo event handlers.
 */
export var WjInputDateTime = WjInputDateTimeBehavior.register();
function registerV3WjInputDateTime(app) {
    app.component(WjInputDateTimeBehavior.tag, WjInputDateTime);
}
var WjInputDateRangeBehavior = /** @class */ (function (_super) {
    __extends(WjInputDateRangeBehavior, _super);
    function WjInputDateRangeBehavior() {
        return _super !== null && _super.apply(this, arguments) || this;
    }
    WjInputDateRangeBehavior.tag = 'wj-input-date-range';
    WjInputDateRangeBehavior.props = [
        'isDisabled',
        'tabOrder',
        'isDroppedDown',
        'showDropDownButton',
        'autoExpandSelection',
        'placeholder',
        'dropDownCssClass',
        'isAnimated',
        'isReadOnly',
        'isRequired',
        'inputType',
        'clickAction',
        'selectionMode',
        'format',
        'mask',
        'max',
        'min',
        'inputType',
        'repeatButtons',
        'showYearPicker',
        'itemValidator',
        'itemFormatter',
        'monthCount',
        'handleWheel',
        'showMonthPicker',
        'showHeader',
        'weeksBefore',
        'weeksAfter',
        'rangeMin',
        'rangeMax',
        'separator',
        'alwaysShowCalendar',
        'predefinedRanges',
        'closeOnSelection',
        'text',
        'value',
        'rangeEnd',
        'preserveCalendarNavigation'
    ];
    WjInputDateRangeBehavior.events = [
        'initialized',
        'gotFocus',
        'lostFocus',
        'refreshing',
        'refreshed',
        'invalidInput',
        'isDroppedDownChanging',
        'rangeChanged'
    ];
    WjInputDateRangeBehavior.changeEvents = {
        'isDroppedDownChanged': ['isDroppedDown'],
        'textChanged': ['text'],
        'valueChanged': ['value'],
        'rangeEndChanged': ['rangeEnd'],
    };
    WjInputDateRangeBehavior.classCtor = function () { return wjcInput.InputDateRange; };
    WjInputDateRangeBehavior.modelProp = 'value';
    return WjInputDateRangeBehavior;
}(WjComponentBehavior));
/**
 * Vue component for the {@link wijmo.input.InputDateRange} control.
 *
 * The component supports all properties and events of the pure JavaScript {@link wijmo.input.InputDateRange} control it represents.
 *
 * The component includes an <b>initialized</b> event that is raised when the control is initialized after it is added to the page.
 * You can use this event to perform further initialization in addition to setting properties in markup.
 * The signature of the handler function is the same as any other Wijmo event handlers.
 */
export var WjInputDateRange = WjInputDateRangeBehavior.register();
function registerV3WjInputDateRange(app) {
    app.component(WjInputDateRangeBehavior.tag, WjInputDateRange);
}
var WjMenuBehavior = /** @class */ (function (_super) {
    __extends(WjMenuBehavior, _super);
    function WjMenuBehavior() {
        return _super !== null && _super.apply(this, arguments) || this;
    }
    Object.defineProperty(WjMenuBehavior.prototype, "value", {
        get: function () {
            return this._value;
        },
        set: function (value) {
            this._value = value;
            if (value != null) {
                this.control.selectedValue = value;
                this._updateHeader();
            }
        },
        enumerable: true,
        configurable: true
    });
    WjMenuBehavior.prototype._createControl = function () {
        var _this = this;
        var ctrl = _super.prototype._createControl.call(this);
        ctrl.itemsSource = new wjcCore.ObservableArray();
        ctrl.selectedIndex = 0;
        ctrl.listBox.formatItem.addHandler(this._fmtItem, this);
        ctrl.invalidate();
        ctrl.itemClicked.addHandler(function (e) {
            _this.value = _this.control.selectedValue;
        });
        return ctrl;
    };
    WjMenuBehavior.prototype._updateControl = function (property, newValue) {
        _super.prototype._updateControl.call(this, property, newValue);
        if (property === 'header') {
            this._definedHeader = newValue;
            this._updateHeader();
        }
        if (property === 'value') {
            this.value = newValue;
        }
    };
    WjMenuBehavior.prototype._updateHeader = function () {
        this.control.header = this._definedHeader || '';
        var selItem = this.control.selectedItem;
        if (this.value != null && selItem && this.control.displayMemberPath) {
            var currentValue = null;
            if (selItem instanceof WjMenuItemBehavior) {
                var contentRoot = selItem.contentRoot;
                if (contentRoot) {
                    currentValue = contentRoot.innerHTML;
                }
                else {
                    currentValue = selItem[this.control.displayMemberPath];
                }
            }
            if (currentValue != null) {
                this.control.header += ': <b>' + currentValue + '</b>';
            }
        }
    };
    WjMenuBehavior.prototype._fmtItem = function (s, e) {
        if (!(e.data instanceof WjMenuItemBehavior
            || e.data instanceof WjMenuSeparatorBehavior)) {
            return;
        }
        e.item.textContent = '';
        e.item.appendChild(e.data.contentRoot);
        e.data.added(e.item);
    };
    WjMenuBehavior.tag = 'wj-menu';
    WjMenuBehavior.props = [
        'isDisabled',
        'tabOrder',
        'isDroppedDown',
        'showDropDownButton',
        'autoExpandSelection',
        'placeholder',
        'dropDownCssClass',
        'isAnimated',
        'isReadOnly',
        'isRequired',
        'inputType',
        'clickAction',
        'displayMemberPath',
        'selectedValuePath',
        'headerPath',
        'isContentHtml',
        'isEditable',
        'handleWheel',
        'maxDropDownHeight',
        'maxDropDownWidth',
        'itemFormatter',
        'showGroups',
        'trimText',
        'caseSensitiveSearch',
        'virtualizationThreshold',
        'header',
        'commandParameterPath',
        'commandPath',
        'subItemsPath',
        'openOnHover',
        'closeOnLeave',
        'isButton',
        'itemsSource',
        'text',
        'selectedIndex',
        'selectedItem',
        'selectedValue',
        'value',
        'ariaLabel',
        'autoSelectOnFocus'
    ];
    WjMenuBehavior.events = [
        'initialized',
        'gotFocus',
        'lostFocus',
        'refreshing',
        'refreshed',
        'invalidInput',
        'isDroppedDownChanging',
        'itemsSourceChanged',
        'formatItem'
    ];
    WjMenuBehavior.changeEvents = {
        'isDroppedDownChanged': ['isDroppedDown'],
        'textChanged': ['text'],
        'selectedIndexChanged': ['selectedIndex', 'selectedItem', 'selectedValue'],
        'itemClicked': ['value'],
    };
    WjMenuBehavior.classCtor = function () { return wjcInput.Menu; };
    WjMenuBehavior.modelProp = 'selectedValue';
    return WjMenuBehavior;
}(WjComponentBehavior));
/**
 * Vue component for the {@link wijmo.input.Menu} control.
 *
 * The <b>wj-menu</b> component may contain
 * the following child components:
 * {@link wijmo.vue2.input.WjMenuItem}
 * , {@link wijmo.vue2.input.WjMenuSeparator}
 * and {@link wijmo.vue2.input.WjItemTemplate}.
 *
 * The component supports all properties and events of the pure JavaScript {@link wijmo.input.Menu} control it represents.
 *
 * The component includes an <b>initialized</b> event that is raised when the control is initialized after it is added to the page.
 * You can use this event to perform further initialization in addition to setting properties in markup.
 * The signature of the handler function is the same as any other Wijmo event handlers.
 */
export var WjMenu = WjMenuBehavior.register();
function registerV3WjMenu(app) {
    app.component(WjMenuBehavior.tag, WjMenu);
}
var WjMenuItemBehavior = /** @class */ (function (_super) {
    __extends(WjMenuItemBehavior, _super);
    function WjMenuItemBehavior() {
        return _super !== null && _super.apply(this, arguments) || this;
    }
    WjMenuItemBehavior.prototype._createControl = function () {
        var ownerMenu = this.parent.control;
        if (ownerMenu.itemsSource.length == 1 && ownerMenu.selectedIndex < 0) {
            ownerMenu.selectedIndex = 0;
        }
        if (!ownerMenu.displayMemberPath) {
            ownerMenu.displayMemberPath = 'header';
        }
        if (!ownerMenu.selectedValuePath) {
            ownerMenu.selectedValuePath = 'value';
        }
        if (!ownerMenu.commandPath) {
            ownerMenu.commandPath = 'cmd';
        }
        if (!ownerMenu.commandParameterPath) {
            ownerMenu.commandParameterPath = 'cmdParam';
        }
        this.contentRoot = this.component.$el.firstElementChild;
        this.component.$el.style.display = 'none';
        return this;
    };
    WjMenuItemBehavior.prototype.added = function (toItem) {
    };
    WjMenuItemBehavior.tag = 'wj-menu-item';
    WjMenuItemBehavior.parentProp = 'itemsSource';
    WjMenuItemBehavior.siblingId = 'menuItemDir';
    WjMenuItemBehavior.props = [
        'wjProperty',
        'value',
        'cmd',
        'cmdParam'
    ];
    WjMenuItemBehavior.events = [
        'initialized'
    ];
    WjMenuItemBehavior.render = function (createElement) {
        if (VueApi.isV3Plus) {
            var h = VueApi.h;
            var defaultSlot = this.$slots.default;
            return h('div', {}, [h('div', {}, defaultSlot && defaultSlot())]);
        }
        return createElement('div', [createElement('div', [this.$slots.default])]);
    };
    return WjMenuItemBehavior;
}(WjComponentBehavior));
/**
 * Vue component for {@link wijmo.vue2.input.WjMenu} items.
 *
 * The <b>wj-menu-item</b> component should be contained in
 * a {@link wijmo.vue2.input.WjMenu} component.
 *
 * The component supports all properties and events of the pure JavaScript {@link } class it represents.
 *
 * The component includes an <b>initialized</b> event that is raised when the control is initialized after it is added to the page.
 * You can use this event to perform further initialization in addition to setting properties in markup.
 * The signature of the handler function is the same as any other Wijmo event handlers.
 */
export var WjMenuItem = WjMenuItemBehavior.register();
function registerV3WjMenuItem(app) {
    app.component(WjMenuItemBehavior.tag, WjMenuItem);
}
var WjMenuSeparatorBehavior = /** @class */ (function (_super) {
    __extends(WjMenuSeparatorBehavior, _super);
    function WjMenuSeparatorBehavior() {
        return _super !== null && _super.apply(this, arguments) || this;
    }
    WjMenuSeparatorBehavior.prototype._createControl = function () {
        this.contentRoot = this.component.$el.firstElementChild;
        this.component.$el.style.display = 'none';
        return this;
    };
    WjMenuSeparatorBehavior.prototype.added = function (toItem) {
        // prevent item selection
        wjcCore.addClass(toItem, wjcCore.ControlStateClsNames.disabled);
        // Added role as separator. WJM-33325
        wjcCore.setAttribute(toItem, "role", "separator", false);
    };
    WjMenuSeparatorBehavior.tag = 'wj-menu-separator';
    WjMenuSeparatorBehavior.parentProp = 'itemsSource';
    WjMenuSeparatorBehavior.siblingId = 'menuItemDir';
    WjMenuSeparatorBehavior.props = [
        'wjProperty',
    ];
    WjMenuSeparatorBehavior.events = [
        'initialized'
    ];
    WjMenuSeparatorBehavior.render = function (createElement) {
        var style = {
            width: '100%',
            height: '1px',
            'background-color': 'lightgray',
        };
        if (VueApi.isV3Plus) {
            var h = VueApi.h;
            return h('div', {}, [h('div', { style: style })]);
        }
        return createElement('div', [createElement('div', { style: style })]);
    };
    return WjMenuSeparatorBehavior;
}(WjComponentBehavior));
/**
 * Vue component for {@link wijmo.vue2.input.WjMenu} item separators.
 *
 * The <b>wj-menu-separator</b> component should be contained in
 * a {@link wijmo.vue2.input.WjMenu} component.
 *
 * The component supports all properties and events of the pure JavaScript {@link } class it represents.
 *
 * The component includes an <b>initialized</b> event that is raised when the control is initialized after it is added to the page.
 * You can use this event to perform further initialization in addition to setting properties in markup.
 * The signature of the handler function is the same as any other Wijmo event handlers.
 */
export var WjMenuSeparator = WjMenuSeparatorBehavior.register();
function registerV3WjMenuSeparator(app) {
    app.component(WjMenuSeparatorBehavior.tag, WjMenuSeparator);
}
var WjItemTemplateDefinition = {
    render: function (createElement) {
        return VueApi.isV3Plus
            ? VueApi.h('div')
            : createElement('div');
    },
    mounted: function () {
        var _this = this;
        var parentComponent = this.$parent;
        if (parentComponent) {
            var parentBeh_1 = parentComponent[WjComponentBehavior._behProp];
            parentBeh_1._mountedCB(function () {
                var parentControl = parentBeh_1.control;
                _this.ownerControl = parentControl instanceof wjcInput.ListBox
                    ? parentControl
                    : (parentControl.listBox instanceof wjcInput.ListBox
                        ? parentControl.listBox
                        : undefined);
                _this.itemComponents = [];
                var itemTemplate = VueApi.isV3Plus
                    ? _this.$slots.default
                    : _this.$scopedSlots.default;
                var ownerControl = _this.ownerControl;
                if (ownerControl && itemTemplate) {
                    _this.formatItemHandler = function (sender, e) {
                        var itemIndex = e.index;
                        if (!_this.itemComponents[itemIndex]) {
                            if (VueApi.isV3Plus) {
                                var h_1 = VueApi.h;
                                var itemComponent = {
                                    parent: _this.component,
                                    render: function () {
                                        return h_1('div', {}, itemTemplate && itemTemplate({
                                            itemIndex: itemIndex,
                                            item: e.data,
                                            control: ownerControl,
                                        }));
                                    }
                                };
                                var vnode = h_1(itemComponent);
                                _this.itemComponents[itemIndex] = e.item;
                                e.item.textContent = '';
                                VueApi.render(vnode, e.item);
                            }
                            else {
                                var itemComponent = ItemTemplateComponentFactory(itemTemplate, {
                                    itemIndex: itemIndex,
                                    item: e.data,
                                    control: ownerControl,
                                }, _this).$mount();
                                _this.itemComponents[itemIndex] = itemComponent;
                                e.item.textContent = '';
                                e.item.appendChild(itemComponent.$el);
                            }
                        }
                    };
                    ownerControl.formatItem.addHandler(_this.formatItemHandler, _this);
                    ownerControl.loadingItems.addHandler(function (sender) {
                        _this._destroyItemComponents();
                    });
                }
            });
        }
    },
    methods: {
        _destroyItemComponents: function () {
            (this.itemComponents || []).forEach(function (item) {
                if (VueApi.isV3Plus) {
                    VueApi.render(null, item); // destroy
                }
                else {
                    item.$destroy();
                }
            });
            this.itemComponents = [];
        },
    },
};
WjItemTemplateDefinition[VueApi.isV3Plus ? 'unmounted' : 'destroyed'] = function () {
    if (this.formatItemHandler) {
        this.ownerControl.formatItem.removeHandler(this.formatItemHandler, this);
    }
    this._destroyItemComponents();
};
var ItemTemplateComponentFactory = function (itemTemplate, propsData, parent) {
    var component = new ItemTemplateComponent({ parent: parent });
    component.itemTemplate = itemTemplate;
    component.itemData = propsData;
    return component;
};
var ItemTemplateComponent = VueApi.extend({
    render: function (createElement) {
        return createElement('div', {}, this.itemTemplate && this.itemTemplate(this.itemData));
    },
});
var WjItemTemplateTag = 'wj-item-template';
/**
 * Vue component to define item templates for item
 * controls like {@link ListBox}, {@link ComboBox}, {@link MultiSelect}
 * and  {@link Menu}.
 */
export var WjItemTemplate = VueApi.isV3Plus
    ? WjItemTemplateDefinition
    : VueApi.component(WjItemTemplateTag, WjItemTemplateDefinition);
function registerV3WjItemTemplate(app) {
    app.component(WjItemTemplateTag, WjItemTemplate);
}
var WjPopupBehavior = /** @class */ (function (_super) {
    __extends(WjPopupBehavior, _super);
    function WjPopupBehavior() {
        return _super !== null && _super.apply(this, arguments) || this;
    }
    WjPopupBehavior.tag = 'wj-popup';
    WjPopupBehavior.props = [
        'isDisabled',
        'tabOrder',
        'owner',
        'showTrigger',
        'hideTrigger',
        'fadeIn',
        'fadeOut',
        'isDraggable',
        'isResizable',
        'dialogResultEnter',
        'dialogResultSubmit',
        'modal',
        'removeOnHide'
    ];
    WjPopupBehavior.events = [
        'initialized',
        'gotFocus',
        'lostFocus',
        'refreshing',
        'refreshed',
        'invalidInput',
        'showing',
        'shown',
        'hiding',
        'hidden',
        'resizing',
        'sizeChanging',
        'sizeChanged',
        'resized',
        'dragging',
        'positionChanging',
        'positionChanged',
        'dragged'
    ];
    WjPopupBehavior.classCtor = function () { return wjcInput.Popup; };
    return WjPopupBehavior;
}(WjComponentBehavior));
/**
 * Vue component for the {@link wijmo.input.Popup} control.
 *
 * The component supports all properties and events of the pure JavaScript {@link wijmo.input.Popup} control it represents.
 *
 * The component includes an <b>initialized</b> event that is raised when the control is initialized after it is added to the page.
 * You can use this event to perform further initialization in addition to setting properties in markup.
 * The signature of the handler function is the same as any other Wijmo event handlers.
 */
export var WjPopup = WjPopupBehavior.register();
function registerV3WjPopup(app) {
    app.component(WjPopupBehavior.tag, WjPopup);
}
var _CtxMenuUtils = /** @class */ (function () {
    function _CtxMenuUtils() {
        this.key = '__wjCtxMenu';
    }
    Object.defineProperty(_CtxMenuUtils.prototype, "definition", {
        get: function () {
            return VueApi.isV3Plus
                ? {
                    mounted: this._bind.bind(this),
                    updated: this._update.bind(this),
                    unmounted: this._unbind.bind(this),
                }
                : {
                    bind: this._bind.bind(this),
                    update: this._update.bind(this),
                    unbind: this._unbind.bind(this),
                };
        },
        enumerable: true,
        configurable: true
    });
    _CtxMenuUtils.prototype._bind = function (el, binding, vnode) {
        var _this = this;
        var listener = function (e) {
            // don't show menu for disabled elements
            if (wjcCore.closest(e.target, '[disabled]')) {
                return;
            }
            // find appropriate menu dynamically
            var menu = _this._getMenuControl(binding, vnode);
            // show menu if possible
            if (menu && menu.dropDown) {
                e.preventDefault();
                e.stopPropagation();
                menu.owner = el;
                menu.show(e);
            }
        };
        el.addEventListener('contextmenu', listener);
        el[this.key] = { binding: binding, listener: listener };
    };
    _CtxMenuUtils.prototype._unbind = function (el) {
        var listener = el[this.key] && el[this.key].listener;
        listener && el.removeEventListener('contextmenu', listener);
        el[this.key] = null;
    };
    _CtxMenuUtils.prototype._update = function (el, binding, vnode) {
        var prevBinding = el[this.key] && el[this.key].binding;
        if (!prevBinding || (prevBinding.value !== binding.value)) {
            this._unbind(el);
            this._bind(el, binding, vnode);
        }
    };
    _CtxMenuUtils.prototype._getMenuControl = function (binding, vnode) {
        var menu = null, el = null, value = binding.value;
        // handle $refs in binding.expression
        if (value == null && binding.expression.indexOf('$refs.') === 0) {
            var bnd = new wjcCore.Binding(binding.expression);
            value = bnd.getValue(vnode.context);
        }
        if (wjcCore.isString(value)) {
            el = document.getElementById(value);
        }
        else if (value instanceof HTMLElement) {
            el = value;
        }
        if (el) {
            menu = wjcCore.Control.getControl(el);
        }
        else {
            // menu = value instanceof Vue ? value.control : value;
            menu = (value && value.control) || value;
        }
        return wjcCore.tryCast(menu, wjcInput.Menu);
    };
    return _CtxMenuUtils;
}());
var wjContextMenuDefinition = (new _CtxMenuUtils()).definition;
var WjContextMenuTag = 'wjContextMenu';
/**
 * Vue directive to define context menus for elements.
 * TBD: description goes here...
 **/
export var WjContextMenu = VueApi.isV3Plus
    ? wjContextMenuDefinition
    : VueApi.directive(WjContextMenuTag, wjContextMenuDefinition);
function registerV3WjContextMenu(app) {
    app.directive(WjContextMenuTag, WjContextMenu);
    ;
}
var WjCollectionViewNavigatorBehavior = /** @class */ (function (_super) {
    __extends(WjCollectionViewNavigatorBehavior, _super);
    function WjCollectionViewNavigatorBehavior() {
        return _super !== null && _super.apply(this, arguments) || this;
    }
    WjCollectionViewNavigatorBehavior.tag = 'wj-collection-view-navigator';
    WjCollectionViewNavigatorBehavior.props = [
        'isDisabled',
        'tabOrder',
        'cv',
        'byPage',
        'headerFormat',
        'repeatButtons',
        'ariaLabel'
    ];
    WjCollectionViewNavigatorBehavior.events = [
        'initialized',
        'gotFocus',
        'lostFocus',
        'refreshing',
        'refreshed',
        'invalidInput'
    ];
    WjCollectionViewNavigatorBehavior.classCtor = function () { return wjcInput.CollectionViewNavigator; };
    return WjCollectionViewNavigatorBehavior;
}(WjComponentBehavior));
/**
 * Vue component for the {@link wijmo.input.CollectionViewNavigator} control.
 *
 * The component supports all properties and events of the pure JavaScript {@link wijmo.input.CollectionViewNavigator} control it represents.
 *
 * The component includes an <b>initialized</b> event that is raised when the control is initialized after it is added to the page.
 * You can use this event to perform further initialization in addition to setting properties in markup.
 * The signature of the handler function is the same as any other Wijmo event handlers.
 */
export var WjCollectionViewNavigator = WjCollectionViewNavigatorBehavior.register();
function registerV3WjCollectionViewNavigator(app) {
    app.component(WjCollectionViewNavigatorBehavior.tag, WjCollectionViewNavigator);
}
export function registerInput(app) {
    if (VueApi.isV3Plus) {
        registerV3WjListBox(app);
        registerV3WjMultiSelectListBox(app);
        registerV3WjComboBox(app);
        registerV3WjAutoComplete(app);
        registerV3WjCalendar(app);
        registerV3WjColorPicker(app);
        registerV3WjInputMask(app);
        registerV3WjInputColor(app);
        registerV3WjMultiSelect(app);
        registerV3WjMultiAutoComplete(app);
        registerV3WjInputNumber(app);
        registerV3WjInputDate(app);
        registerV3WjInputTime(app);
        registerV3WjInputDateTime(app);
        registerV3WjInputDateRange(app);
        registerV3WjMenu(app);
        registerV3WjMenuItem(app);
        registerV3WjMenuSeparator(app);
        registerV3WjItemTemplate(app);
        registerV3WjPopup(app);
        registerV3WjContextMenu(app);
        registerV3WjCollectionViewNavigator(app);
    }
}
